// blocked-ticket-verdict-observability.ts
//
// Plan 20260924-0150 phase-02. `avatar autopilot run` ends with one line,
// `Mission …: outcome=<x> sau N tick.`, and until this module the reason a
// ticket was blocked lived only in `state.ticket_last_verdict` — visible to
// `avatar autopilot status`, invisible to the person watching the run.
//
// Measured on real missions (plan 20260923-2152):
//   - ME-2026-6a9f23: guard layer 2 escalated with a precise sentence
//     ("attempt sửa acceptance của chính ticket T-0102 …"); console showed
//     `outcome=blocked sau 1 tick`.
//   - ME-2026-6d0581: claude rejected its argv 3/3 times; console showed
//     `outcome=idle sau 3 tick` while the only ticket sat `blocked`.
// The second case is why this keys on TICKET status, not on the run outcome.
//
// Same shape as g2-unenforced-conditions-observability.ts: a pure describer
// that stays silent on the steady state, plus the one I/O step the caller needs.

import { promises as fs } from "node:fs";
import type { TicketVerdictRecord } from "./mission-state-reducer.js";
import { openMissionStoreReadOnly } from "./mission-store-read-only.js";
import { bindingPathFor } from "./orphaned-ticket-reconciler.js";

/**
 * One operator-facing line per `blocked` ticket; `[]` when nothing is blocked.
 * A blocked ticket with no verdict of its own (a dependent of a cancelled
 * ticket) is still named — silence there would read as "nothing blocked".
 */
export function describeBlockedTicketVerdicts(
  ticketStatus: Readonly<Record<string, string>>,
  verdicts: Readonly<Record<string, TicketVerdictRecord>>,
): string[] {
  return Object.entries(ticketStatus)
    .filter(([, status]) => status === "blocked")
    .map(([ticketId]) => {
      const verdict = verdicts[ticketId];
      if (verdict === undefined) {
        return `Ticket ${ticketId} blocked — không có verdict riêng (thường do phụ thuộc bị huỷ); xem 'avatar autopilot status'.`;
      }
      const detail = verdict.schema_error_detail ? ` [${verdict.schema_error_detail}]` : "";
      return `Ticket ${ticketId} blocked (${verdict.category}): ${verdict.reasons.join(" · ")}${detail}`;
    });
}

/**
 * Reads the mission state after `run` returned (machine lock already
 * released) through the same lock-free reader `status` uses. Any read failure
 * yields `[]`: this is a courtesy line, and it must never turn a finished run
 * into a crashed one.
 */
export async function readBlockedTicketVerdictLines(controlRoot: string): Promise<string[]> {
  try {
    const store = await openMissionStoreReadOnly(controlRoot);
    try {
      return describeBlockedTicketVerdicts(store.state.tickets, store.state.ticket_last_verdict);
    } finally {
      await store.close();
    }
  } catch {
    return [];
  }
}

/**
 * BUG-2 (dogfood 260925 luồng 4): `outcome=blocked` used to print NOTHING
 * when the reason was a ticket stuck "in_progress" (scheduler.ts's
 * nextReadyTicket refuses to dispatch anything while any ticket is
 * "in_progress", §5.2 one-worktree-one-lease) — `describeBlockedTicketVerdicts`
 * above only ever looks at ticket status "blocked", so an orphaned attempt
 * (post-SIGKILL, post-unlock) read as a silent hang for up to ~61 min. Named
 * even when the binding lease's `expires_at` is unknown — silence here is
 * exactly the bug.
 */
export function describeInProgressTicketReasons(
  ticketStatus: Readonly<Record<string, string>>,
  expiresAtByTicket: Readonly<Record<string, string>>,
): string[] {
  return Object.entries(ticketStatus)
    .filter(([, status]) => status === "in_progress")
    .map(([ticketId]) => {
      const expiresAt = expiresAtByTicket[ticketId];
      return expiresAt
        ? `Ticket ${ticketId} còn "in_progress" — nếu attempt đã mồ côi (vd sau SIGKILL + ` +
            `\`avatar autopilot unlock\`), tự gỡ lúc ${expiresAt}; chạy lại \`avatar autopilot run\` sau đó.`
        : `Ticket ${ticketId} còn "in_progress" — không đọc được binding lease để biết khi nào tự gỡ; ` +
            "xem `avatar autopilot status` hoặc `avatar autopilot unlock` nếu supervisor đã chết.";
    });
}

/**
 * Reads EVERY `in_progress` ticket's binding `expires_at` (best-effort, same
 * lock-free posture as `readBlockedTicketVerdictLines`) and describes each —
 * only the CURRENT mission binding path exists (§5.2 one attempt at a time),
 * so every in_progress ticket shares the same lease file.
 */
export async function readInProgressTicketLines(
  controlRoot: string,
  missionWorkspace: string,
): Promise<string[]> {
  try {
    const store = await openMissionStoreReadOnly(controlRoot);
    let ticketStatus: Readonly<Record<string, string>>;
    try {
      ticketStatus = store.state.tickets;
    } finally {
      await store.close();
    }
    const hasInProgress = Object.values(ticketStatus).some((s) => s === "in_progress");
    if (!hasInProgress) return [];

    const expiresAtByTicket: Record<string, string> = {};
    try {
      const raw = JSON.parse(await fs.readFile(bindingPathFor(missionWorkspace), "utf8")) as {
        ticket_id?: string;
        expires_at?: string;
      };
      if (raw.ticket_id && raw.expires_at) expiresAtByTicket[raw.ticket_id] = raw.expires_at;
    } catch {
      // No binding file, or unreadable — describeInProgressTicketReasons
      // still names the ticket without a self-recovery time.
    }
    return describeInProgressTicketReasons(ticketStatus, expiresAtByTicket);
  } catch {
    return [];
  }
}
